import { useRef, useState } from 'react'
import App from 'antd/es/app/index'
import { useTranslation } from 'react-i18next'
import { useTableHeight } from '@/common/hooks/use-table-height.ts'
import IdeyaModal from '@/common/antd/ideya-modal.tsx'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import {
  MailListControls,
  MailListFilters,
} from './components/mail-list-controls.tsx'
import { MailListTable } from './components/mail-list-table.tsx'
import {
  MailListEditTarget,
  MailListModal,
  MailListModalSubmit,
} from './components/mail-list-modal.tsx'
import {
  useAddMailEntryMutation,
  useEditMailEntryMutation,
  useToggleMailEntryMutation,
} from '@/apis/mail-list/mail-list-api.api.ts'
import { MailListEntry } from '@/apis/mail-list/mail-list-api.def.ts'
import { MailListType } from '@/enums/mail-list.enum.ts'
import { GridRef } from '@/common/ag-grid/grid.types.ts'

const emptyFilters: MailListFilters = {
  firmId: '',
  firmRole: '',
  category: '',
  groupRole: '',
  searchName: '',
}

export const MailList = () => {
  const { t } = useTranslation()
  const { message: messageApi } = App.useApp()
  const [listType, setListType] = useState<MailListType>(MailListType.Block)
  const [filters, setFilters] = useState<MailListFilters>(emptyFilters)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editTarget, setEditTarget] = useState<MailListEditTarget | null>(null)
  const [removeTarget, setRemoveTarget] = useState<string | null>(null)

  const headerRef = useRef<HTMLDivElement>(null)
  const tableHeight = useTableHeight(headerRef)
  const gridRef = useRef<GridRef>(null)

  const [addMailEntry, { isLoading: isAdding }] = useAddMailEntryMutation()
  const [editMailEntry, { isLoading: isEditing }] = useEditMailEntryMutation()
  const [toggleMailEntry, { isLoading: isRemoving }] =
    useToggleMailEntryMutation()

  const handleFilterChange = (patch: Partial<MailListFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch }))

  const handleCreate = () => {
    setModalMode('create')
    setEditTarget(null)
    setShowModal(true)
  }

  const handleEdit = (email: string) => {
    const api = gridRef.current?.getGridApi()
    let entry: MailListEntry | undefined
    api?.forEachNode((node) => {
      if (node.data?.email === email) entry = node.data
    })
    if (!entry) return
    setModalMode('edit')
    setEditTarget({
      id: entry.id,
      email: entry.email,
      userId: entry.userId,
      fullName: entry.fullName,
      listType: entry.listType,
    })
    setShowModal(true)
  }

  const handleRemove = (email: string) => {
    // Right-click asks for confirmation first; the actual removal runs in
    // handleConfirmRemove once the user accepts.
    setRemoveTarget(email)
  }

  const handleConfirmRemove = async () => {
    if (!removeTarget) return
    // Right-click removes the email from the list currently in view only; the
    // backend deletes the row when it no longer belongs to any list. The global
    // base query surfaces failures, so guard against the rejected promise.
    try {
      await toggleMailEntry({ email: removeTarget, listType }).unwrap()
      gridRef.current?.refresh()
      setRemoveTarget(null)
      messageApi.open({
        type: 'success',
        content: t('mail_list.feedback.remove_success'),
      })
    } catch {
      // Error notification is handled by the shared base query; keep the
      // confirm open so the user can retry.
    }
  }

  const handleSubmit = async (input: MailListModalSubmit) => {
    try {
      if (modalMode === 'edit' && editTarget) {
        await editMailEntry({
          id: editTarget.id,
          listType: input.listType,
        }).unwrap()
      } else {
        await addMailEntry({
          userId: input.userId,
          email: input.email,
          listType: input.listType,
        }).unwrap()
      }
      gridRef.current?.refresh()
      setShowModal(false)
      setEditTarget(null)
      messageApi.open({
        type: 'success',
        content: t(
          modalMode === 'edit'
            ? 'mail_list.feedback.edit_success'
            : 'mail_list.feedback.add_success'
        ),
      })
    } catch {
      // Keep the modal open so the user can retry; the shared base query already
      // shows the error notification.
    }
  }

  // Explicit removal from the edit modal: toggle the email off its list; the
  // backend deletes the row once it no longer belongs to any list.
  const handleRemoveFromModal = async () => {
    if (!editTarget) return
    try {
      await toggleMailEntry({
        email: editTarget.email,
        listType: editTarget.listType,
      }).unwrap()
      gridRef.current?.refresh()
      setShowModal(false)
      setEditTarget(null)
      messageApi.open({
        type: 'success',
        content: t('mail_list.feedback.remove_success'),
      })
    } catch {
      // Shared base query surfaces the error; keep the modal open to retry.
    }
  }

  // The chosen email already belongs to a list: switch the open modal to edit
  // that existing entry instead of creating a duplicate.
  const handleExistingEntry = (entry: MailListEntry) => {
    setModalMode('edit')
    setEditTarget({
      id: entry.id,
      email: entry.email,
      userId: entry.userId ?? undefined,
      fullName: entry.fullName ?? undefined,
      listType: entry.listType,
    })
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditTarget(null)
  }

  return (
    <>
      <div ref={headerRef}>
        <MailListControls
          listType={listType}
          onListTypeChange={setListType}
          filters={filters}
          onFilterChange={handleFilterChange}
          onCreate={handleCreate}
        />
      </div>
      <MailListTable
        ref={gridRef}
        listType={listType}
        filters={filters}
        onEditEntry={handleEdit}
        onRemoveEntry={handleRemove}
        height={tableHeight}
      />
      <MailListModal
        open={showModal}
        mode={modalMode}
        editTarget={editTarget}
        isSubmitting={isAdding || isEditing || isRemoving}
        onCancel={handleCloseModal}
        onSubmit={handleSubmit}
        onRemove={handleRemoveFromModal}
        onExistingEntry={handleExistingEntry}
      />
      <IdeyaModal
        open={removeTarget !== null}
        title={t('mail_list.modal.remove_confirm_title')}
        destroyOnHidden
        confirmLoading={isRemoving}
        onOk={handleConfirmRemove}
        onCancel={() => setRemoveTarget(null)}
      >
        <IdeyaTypography.Text>
          {t('mail_list.modal.remove_from_list_confirm_message', {
            list: t(
              listType === MailListType.White
                ? 'mail_list.controls.white_list'
                : 'mail_list.controls.block_list'
            ),
          })}
        </IdeyaTypography.Text>
      </IdeyaModal>
    </>
  )
}
